<script lang="ts" setup>
import { isDark } from '@/utils/is'
import { useAppStore } from '@/store/modules/app'
import { useDesign } from '@/hooks/web/useDesign'
import { CACHE_KEY, useCache } from '@/hooks/web/useCache'
import routerSearch from '@/components/RouterSearch/index.vue'

defineOptions({ name: 'APP' })

const { getPrefixCls } = useDesign()
const prefixCls = getPrefixCls('app')
const appStore = useAppStore()
const currentSize = computed(() => appStore.getCurrentSize)
const greyMode = computed(() => appStore.getGreyMode)
const { wsCache } = useCache()

// 根据浏览器当前主题设置系统主题色
const setDefaultTheme = () => {
  let isDarkTheme = wsCache.get(CACHE_KEY.IS_DARK)
  if (isDarkTheme === null) {
    isDarkTheme = isDark()
  }
  appStore.setIsDark(isDarkTheme)
}
setDefaultTheme()
</script>
<template>
  <ConfigGlobal :size="currentSize">
    <RouterView :class="greyMode ? `${prefixCls}-grey-mode` : ''" />
    <routerSearch />
  </ConfigGlobal>
</template>
<style lang="scss">
$prefix-cls: #{$namespace}-app;

.size {
  width: 100%;
  height: 100%;
}

html,
body {
  @extend .size;

  padding: 0 !important;
  margin: 0;
  overflow: hidden;

  #app {
    @extend .size;
  }
}

.#{$prefix-cls}-grey-mode {
  filter: grayscale(100%);
}

/* ===== 全局：去除所有线条，改为色块区分 ===== */
/* el-card 去除边框和阴影 */
.el-card {
  border: none;
  box-shadow: none;
}

/* layout-border 线条全部隐藏 */
.layout-border__bottom:after,
.layout-border__top:before,
.layout-border__left:before,
.layout-border__right:after {
  display: none !important;
}

/* TagsView 工具按钮分隔线 */
.v-tags-view__tool::before,
.v-tags-view__tool--first::before {
  border-left: none;
  border-right: none;
}

/* Element Plus 全局：输入框/选择框等表单组件使用浅灰背景替代边框 */
.el-input__wrapper,
.el-textarea__inner,
.el-select .el-input__wrapper,
.el-date-editor .el-input__wrapper,
.el-input-number .el-input__wrapper,
.el-cascader .el-input__wrapper {
  background-color: var(--input-bg-color) !important;
  box-shadow: none !important;
  border-radius: 6px;
}

/* 输入框 hover/focus 时仍然显示一点视觉反馈 */
.el-input__wrapper:hover,
.el-textarea__inner:hover {
  background-color: #f0f0f0 !important;
}
.el-input.is-focus .el-input__wrapper,
.el-select .el-input.is-focus .el-input__wrapper,
.el-textarea__inner:focus {
  background-color: #ffffff !important;
  box-shadow: 0 0 0 1px var(--el-color-primary) inset !important;
}

/* 表格去掉所有边框 */
.el-table,
.el-table__header-wrapper th,
.el-table__body-wrapper td {
  border: none !important;
}
.el-table--border::after,
.el-table--group::after,
.el-table::before {
  background-color: transparent !important;
}
.el-table--border .el-table__cell {
  border: none !important;
}
/* 表格行hover背景 */
.el-table tbody tr:hover > td {
  background-color: #f8f9fc !important;
}

/* 分页去掉边框 */
.el-pagination .btn-prev,
.el-pagination .btn-next,
.el-pagination button {
  background-color: var(--input-bg-color) !important;
  border: none !important;
  border-radius: 6px;
}

/* ===== 下拉框/选择器面板与白色背景区分 ===== */
/* 下拉面板整体背景色 */
.el-select-dropdown,
.el-select-dropdown__list,
.el-cascader__dropdown,
.el-cascader-panel,
.el-date-picker,
.el-time-panel,
.el-time-picker__panel,
.el-picker-panel,
.el-dropdown-menu,
.el-autocomplete-suggestion {
  background-color: #fafbfc !important;
  border: none !important;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06) !important;
}

/* 下拉选项hover */
.el-select-dropdown__item:hover,
.el-select-dropdown__item.is-hovering,
.el-select-dropdown__item.is-selected {
  background-color: #f0edff !important;
}
.el-select-dropdown__item.is-selected {
  color: var(--el-color-primary);
  font-weight: 500;
}

/* 级联选择器 */
.el-cascader-node:not(.is-disabled):hover,
.el-cascader-node.is-selectable:not(.is-disabled):hover {
  background-color: #f0edff !important;
}

/* Tag 标签 */
.el-tag {
  border: none !important;
}

/* Tabs */
.el-tabs__nav-wrap::after {
  background-color: transparent !important;
}
.el-tabs__item {
  border: none !important;
}
.el-tabs__active-bar {
  border-radius: 2px;
}

/* ===== 按钮：默认按钮用灰色背景与白色区分 ===== */
/* 默认按钮（非纯文字/非主要/非虚线） */
.el-button:not(.el-button--text):not(.el-button--primary):not(.el-button--success):not(
    .el-button--warning
  ):not(.el-button--danger):not(.el-button--info) {
  background-color: var(--input-bg-color);
  border-color: transparent !important;
  color: #303133;
}
.el-button:not(.el-button--text):not(.el-button--primary):not(.el-button--success):not(
    .el-button--warning
  ):not(.el-button--danger):not(.el-button--info):hover {
  background-color: #eceef3;
  color: var(--el-color-primary);
}

/* 文字按钮保持原样 */
.el-button--text,
.el-button.is-text {
  background-color: transparent !important;
  border: none !important;
}

/* 主要按钮 */
.el-button--primary,
.el-button--success,
.el-button--warning,
.el-button--danger,
.el-button--info {
  border-color: transparent !important;
}

/* 按钮组间距 */
.el-button-group .el-button {
  border-color: transparent !important;
}
.el-button-group .el-button:not(:last-child) {
  margin-right: 1px;
}

/* 分割线组件 */
.el-divider {
  border-color: #eceef3 !important;
}

/* 抽屉 */
.el-drawer__header {
  border-bottom: none !important;
}
.el-drawer__footer {
  border-top: none !important;
}

/* 弹出框 */
.el-popover {
  border: none !important;
}

/* 下拉菜单 */
.el-dropdown-menu__item:not(.is-disabled):hover {
  background-color: #f0edff !important;
}

/* 树形控件 */
.el-tree-node__content:hover {
  background-color: #f8f9fc !important;
}

/* ===== 基础选择控件：白色背景下保留清晰的边界和轨道 ===== */
.el-radio__inner,
.el-checkbox__inner {
  background-color: var(--el-bg-color) !important;
  border: 1px solid var(--control-border-color) !important;
}

.el-radio__input:not(.is-disabled):hover .el-radio__inner,
.el-checkbox__input:not(.is-disabled):hover .el-checkbox__inner {
  border-color: var(--el-color-primary) !important;
}

.el-radio__input.is-checked .el-radio__inner,
.el-checkbox__input.is-checked .el-checkbox__inner,
.el-checkbox__input.is-indeterminate .el-checkbox__inner {
  background-color: var(--el-color-primary) !important;
  border-color: var(--el-color-primary) !important;
}

.el-radio__input.is-focus .el-radio__inner,
.el-checkbox__input.is-focus .el-checkbox__inner {
  outline: 2px solid var(--el-color-primary-light-7);
  outline-offset: 1px;
}

.el-radio__input.is-disabled .el-radio__inner,
.el-checkbox__input.is-disabled .el-checkbox__inner {
  background-color: var(--control-disabled-color) !important;
  border-color: var(--el-border-color) !important;
}

.el-switch__core {
  background-color: var(--control-track-color) !important;
  border: 1px solid var(--control-border-color) !important;
}

.el-switch.is-checked .el-switch__core {
  background-color: var(--el-color-primary) !important;
  border-color: var(--el-color-primary) !important;
}

.el-slider__runway,
.el-slider__bar {
  height: 6px !important;
}

.el-slider__runway {
  background-color: var(--control-track-color) !important;
  box-shadow: inset 0 0 0 1px var(--el-border-color-light);
}

.el-slider__bar {
  background-color: var(--el-color-primary) !important;
}

.el-slider__button {
  width: 18px;
  height: 18px;
  background-color: var(--el-bg-color);
  border: 3px solid var(--el-color-primary);
  box-shadow: 0 1px 4px rgb(0 0 0 / 18%);
}

.el-progress-bar__outer {
  background-color: var(--control-track-color) !important;
  box-shadow: inset 0 0 0 1px var(--el-border-color-light);
}

.el-progress-bar__inner {
  min-width: 2px;
}

.el-progress__text,
.el-radio__label,
.el-checkbox__label {
  color: var(--el-text-color-regular);
}
</style>
